import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import {
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlinePlusCircle,
    AiOutlineCheckCircle,
    AiOutlineClose,
    AiOutlineSync,
    AiOutlineFile
} from "react-icons/ai";

// ===== REAL API SECTION =====
import { API_CONFIG } from '../../config/env';
const API_BASE_URL = `${API_CONFIG.BASE_URL}/admin/documents`;

async function apiFetchYears() {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/years/`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to fetch years');
    }
    
    const data = await response.json();
    return data.data || [];
}

async function apiAddYear({ year }) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/years/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            year: Number(year)
        })
    });
    
    if (!response.ok) {
        throw new Error('Failed to add year');
    }
    
    return true;
}

async function apiEditYear({ id, year }) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/years/${id}/`, {
        method: 'PUT',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            year: Number(year)
        })
    });
    
    if (!response.ok) {
        throw new Error('Failed to edit year');
    }
    
    return true;
}

async function apiDeleteYear(id) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/years/${id}/`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to delete year');
    }
    
    return true;
}

async function apiChangeYearStatus(id) {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/years/${id}/toggle-status/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    
    if (!response.ok) {
        throw new Error('Failed to change year status');
    }
    
    return true;
}
// ============================

export default function Year() {
    const [table, setTable] = useState([]);
    const [loading, setLoading] = useState(false);

    // Pagination
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Dialogs states
    const [addOpen, setAddOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [deleteRow, setDeleteRow] = useState(null);
    const [success, setSuccess] = useState("");

    // Form states for Add/Edit
    const [addVals, setAddVals] = useState({ year: "" });
    const [addErr, setAddErr] = useState("");
    const [editVals, setEditVals] = useState({ year: "" });
    const [editErr, setEditErr] = useState("");

    // Fetch on mount/update
    async function fetchData() {
        setLoading(true);
        const rows = await apiFetchYears();
        setTable(rows.sort((a, b) => b.year - a.year));
        setLoading(false);
    }
    useEffect(() => { fetchData(); }, []);

    // Responsive: Table columns with minWidth/flex and scroll on small screens
    const columns = [
        {
            name: "S.No",
            width: "150px",
            minWidth: "60px",
            cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
            style: { fontWeight: 600, color: "#4338ca", textAlign: "center" },
            sortable: false,
        },
        {
            name: "Year",
            selector: row => row.year,
            sortable: true,
            minWidth: "120px",
            maxWidth: "250px",
            style: { fontWeight: 660, fontSize: 15.5, textAlign: "center" },
        },
        {
            name: "Status",
            selector: row => row.is_active,
            minWidth: "120px",
            maxWidth: "250px",
            sortable: false,
            cell: row => (
                <span style={{
                    borderRadius: "13px",
                    padding: "5px 22px",
                    //fontWeight: 800,
                    color: "#fff",
                    fontSize: 14.5,
                    background: row.is_active ? "#21bb55" : "#dc2626",
                    boxShadow: row.is_active ? "0 2px 7px #21bb5520" : "0 2px 8px #dc262611",
                    letterSpacing: ".03em",
                    lineHeight: 1.8,
                    transition: ".12s background"
                }}>
                    {row.is_active ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            name: "Actions",
            width: "400px",
            style: { textAlign: "center" },
            cell: row => (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <button onClick={() => openEdit(row)} style={actBtn("#6659fa")}>
                        <AiOutlineEdit /> Edit
                    </button>
                    <button onClick={() => openDelete(row)} style={actBtn("#dc2626")}>
                        <AiOutlineDelete /> Delete
                    </button>
                    <button onClick={() => handleChangeStatus(row)} style={actBtn("#2979f2")}>
                        <AiOutlineSync /> Change Status
                    </button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ];

    // Pagination
    function handlePageChange(page) { setCurrentPage(page); }
    function handlePerRowsChange(newPerPage, page) {
        setPerPage(newPerPage);
        setCurrentPage(page);
    }

    // Actions
    function openEdit(row) {
        setEditRow(row);
        setEditVals({ year: row.year.toString() });
        setEditErr("");
    }
    function openDelete(row) { setDeleteRow(row); }
    async function handleChangeStatus(row) {
        setLoading(true);
        await apiChangeYearStatus(row.id);
        await fetchData();
        setLoading(false);
    }
    // Edit logic
    function onEditChange(e) {
        const { name, value } = e.target;
        setEditVals(vals => ({
            ...vals, [name]: value.replace(/\D/g, "").slice(0, 4)
        }));
        setEditErr("");
    }
    async function handleEditSave(e) {
        e.preventDefault();
        if (!editVals.year || !/^\d{4}$/.test(editVals.year)) {
            setEditErr("Year must be 4 digits.");
            return;
        }
        setLoading(true);
        try {
            await apiEditYear({ id: editRow.id, year: editVals.year });
            await fetchData();
            setEditRow(null);
            showSuccess("Saved successfully!");
        } catch (error) {
            showSuccess("Error: " + error.message);
        }
        setLoading(false);
    }
    // Delete logic
    async function handleDeleteYes() {
        setLoading(true);
        try {
            await apiDeleteYear(deleteRow.id);
            await fetchData();
            setDeleteRow(null);
            showSuccess("Deleted successfully!");
        } catch (error) {
            showSuccess("Error: " + error.message);
        }
        setLoading(false);
    }
    // Add logic
    function onAddChange(e) {
        const { name, value } = e.target;
        setAddVals(vals => ({
            ...vals, [name]: value.replace(/\D/g, "").slice(0, 4)
        }));
        setAddErr("");
    }
    async function handleAdd(e) {
        e.preventDefault();
        if (!/^\d{4}$/.test(addVals.year)) {
            setAddErr("Year must be 4 digits.");
            return;
        }
        setLoading(true);
        try {
            await apiAddYear({ year: addVals.year });
            await fetchData();
            setAddOpen(false);
            setAddVals({ year: "" });
            showSuccess("Saved successfully!");
        } catch (error) {
            showSuccess("Error: " + error.message);
        }
        setLoading(false);
    }

    // Green tick dialog
    function showSuccess(msg) {
        setSuccess(msg);
        setTimeout(() => setSuccess(""), 1300);
    }

    // Table wide responsive styles
    const customStyles = {
        table: {
            style: {
                minWidth: "860px",
                width: "100%",
                overflowX: "auto"
            },
        },
        rows: { style: { fontSize: 15, background: "#f8fafc" } },
        headRow: { style: { fontWeight: 700, fontSize: 15, background: "#eceffd", color: "#4338ca", minHeight: "46px" } },
        headCells: { style: { borderBottom: "1.6px solid #a5b4fc", paddingLeft: "20px", paddingRight: "20px" } },
        cells: { style: { paddingLeft: "20px", paddingRight: "20px" } },
        pagination: { style: { borderTop: "solid 1px #eceffd", color: "#4338ca", fontWeight: 600 } },
    };

    return (
        <section>
            <div style={{
                display: "flex", alignItems: "center", gap: 13, marginBottom: 13,
                flexWrap: "wrap"
            }}>
                <h2 style={{
                    //   fontWeight: 800,
                    //   color: "#2a63b8",
                    //   fontSize: 27,
                    //   letterSpacing: ".045em",
                    //   margin: 0,

                    fontSize: 38,
                    marginRight: 10,
                    color: "#2a63b8",
                    verticalAlign: "middle",
                    filter: "drop-shadow(0px 1px 4px #b5beff77)",
                }}>
                 <AiOutlineFile/>   Year Management
                </h2>
                <button
                    onClick={() => setAddOpen(true)}
                    style={{
                        marginLeft: 'auto',
                        background: "linear-gradient(90deg,#217eed,#49dfa9 100%)",
                        color: "#fff",
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 16,
                        padding: "7px 22px",
                        border: "none",
                        boxShadow: "0 2px 8px #49dfa955",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        transition: ".13s background"
                    }}
                >
                    <AiOutlinePlusCircle size={20} />
                    Add Year
                </button>
            </div>
            {/* Responsive wrapper for table */}
            <div style={{
                background: "#fff",
                borderRadius: 18,
                boxShadow: "0 5px 24px #a5b4fc22",
                padding: 0,
                overflowX: "auto",
                maxWidth: "100vw"
            }}>
                <DataTable
                    columns={columns}
                    data={table}
                    highlightOnHover
                    striped
                    pagination
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onChangePage={handlePageChange}
                    paginationDefaultPage={currentPage}
                    progressPending={loading}
                    noHeader
                    responsive
                    persistTableHead
                    customStyles={customStyles}
                />
            </div>

            {/* Add Modal */}
            {addOpen && (
                <Modal onClose={() => setAddOpen(false)} title="Add Year">
                    <form onSubmit={handleAdd} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                        <label style={labelStyle}>Year <span style={{ color: "#e53545" }}>*</span></label>
                        <input
                            name="year"
                            value={addVals.year}
                            onChange={onAddChange}
                            inputMode="numeric"
                            maxLength={4}
                            autoFocus
                            style={inputStyle}
                            placeholder="e.g., 2028"
                            required
                        />
                        {addErr && <div style={{ color: "#c81d1d", fontWeight: 600, marginBottom: 4 }}>{addErr}</div>}
                        <div style={{ display: "flex", gap: 15, justifyContent: "flex-end" }}>
                            <button type="button" onClick={() => setAddOpen(false)} style={modalBtn("#b7bafe", "#254")}>Cancel</button>
                            <button
                                type="submit"
                                style={{
                                    ...modalBtn("#2497f4", "#fff"),
                                    ...(!/^\d{4}$/.test(addVals.year) ? {
                                        background: "#ccc",
                                        color: "#666",
                                        cursor: "not-allowed",
                                        opacity: 0.6
                                    } : {})
                                }}
                                disabled={!/^\d{4}$/.test(addVals.year)}
                            >Add</button>
                        </div>
                    </form>
                </Modal>
            )}
            {/* Edit Modal */}
            {editRow && (
                <Modal onClose={() => setEditRow(null)} title="Edit Year">
                    <form onSubmit={handleEditSave} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                        <label style={labelStyle}>Year <span style={{ color: "#e53545" }}>*</span></label>
                        <input
                            name="year"
                            value={editVals.year}
                            onChange={onEditChange}
                            inputMode="numeric"
                            maxLength={4}
                            required
                            style={inputStyle}
                        />
                        {editErr && <div style={{ color: "#c81d1d", fontWeight: 600 }}>{editErr}</div>}
                        <div style={{ display: "flex", gap: 15, justifyContent: "flex-end" }}>
                            <button type="button" onClick={() => setEditRow(null)} style={modalBtn("#e3e5f2", "#236")}>Cancel</button>
                            <button
                                type="submit"
                                style={{
                                    ...modalBtn("#2497f4", "#fff"),
                                    ...(!/^\d{4}$/.test(editVals.year) ? {
                                        background: "#ccc",
                                        color: "#666",
                                        cursor: "not-allowed",
                                        opacity: 0.6
                                    } : {})
                                }}
                                disabled={!/^\d{4}$/.test(editVals.year)}
                            >Save</button>
                        </div>
                    </form>
                </Modal>
            )}
            {/* Delete Modal */}
            {deleteRow && (
                <Modal onClose={() => setDeleteRow(null)} title="Delete Year">
                    <div style={{ fontWeight: 500, fontSize: 17, marginBottom: 17 }}>
                        Are you sure you want to delete <span style={{ color: "#3187dd", fontWeight: 800 }}>{deleteRow.year}</span>?
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 15 }}>
                        <button onClick={() => setDeleteRow(null)} style={modalBtn("#cbd3e3", "#288")}>Cancel</button>
                        <button onClick={handleDeleteYes} style={modalBtn("#dc2626", "#fff")}>Delete</button>
                    </div>
                </Modal>
            )}
            {/* Success Dialog */}
            {success && (
                <SuccessModal message={success} onClose={() => setSuccess("")} />
            )}
            {/* Add a responsive media query (optional, for modal input adapt) */}
            <style>
                {`
        @media (max-width: 700px) {
          .rdt_TableCell, .rdt_TableCol {
            padding-left: 10px !important;
            padding-right: 10px !important;
          }
        }
        `}
            </style>
        </section>
    );
}

// ====== Components & Styles ======

function Modal({ onClose, title, children }) {
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modalTitle"
            style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: "rgba(0,0,0,0.23)",
                display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: "#f9fcfe",
                    borderRadius: 15,
                    position: "relative",
                    padding: "30px 28px 15px 28px",
                    minWidth: 320,
                    width: "98vw",
                    maxWidth: 400,
                    boxShadow: "0 9px 40px #6060ec20",
                }}
            >
                <h3 id="modalTitle" style={{ fontWeight: 700, color: "#2970c9", margin: 0, marginBottom: 19, fontSize: 20 }}>
                    {title}
                </h3>
                {children}
                <button
                    aria-label="Close"
                    onClick={onClose}
                    style={{
                        position: "absolute", top: 13, right: 13, background: "transparent",
                        border: "none", fontSize: 23, cursor: "pointer", color: "#666", userSelect: "none",
                    }}
                >
                    <AiOutlineClose />
                </button>
            </div>
        </div>
    );
}
function SuccessModal({ message, onClose }) {
    return (
        <div
            role="alertdialog"
            aria-modal="true"
            onClick={onClose}
            style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: "rgba(0,0,0,0.13)", zIndex: 10001,
                display: "flex", justifyContent: "center", alignItems: "center",
            }}>
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: "#fafff9",
                    padding: "34px 28px 28px 28px",
                    borderRadius: 16,
                    textAlign: "center",
                    boxShadow: "0 8px 40px #53ec8860",
                    color: "#11602c",
                }}>
                <AiOutlineCheckCircle
                    size={62}
                    color="#17bb5f"
                    style={{ marginBottom: 10, animation: "pop-tick .33s cubic-bezier(.19,.7,.68,1.38)" }}
                />
                <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 2 }}>{message}</div>
                <button onClick={onClose} style={modalBtn("#17bb5f", "#fff", 16, 9)}>Close</button>
                <style>{`
          @keyframes pop-tick {
            0% { opacity: 0; transform: scale(0.46);}
            67% { opacity: 1; transform: scale(1.14);}
            100% { opacity: 1; transform: scale(1);}
          }
        `}</style>
            </div>
        </div>
    );
}
function actBtn(bg) {
    return {
        background: bg,
        color: "#fff",
        fontWeight: 700,
        border: "none",
        borderRadius: 8,
        padding: "8px 13px",
        cursor: "pointer",
        fontSize: 14,
        display: "flex",
        alignItems: "center",
        gap: 7,
        minWidth: 68,
        boxShadow: bg === "#2979f2" ? "0 2px 7px #2979f222" : undefined,
        transition: ".12s background"
    };
}
function modalBtn(bg, color, padx = 16, pady = 7) {
    return {
        padding: `${pady}px ${padx}px`,
        background: bg,
        color,
        border: "none",
        borderRadius: 8,
        fontWeight: 700,
        fontSize: 15,
        cursor: "pointer",
        outline: "none"
    };
}
const labelStyle = { color: "#233a70", fontWeight: 700, marginBottom: 3, fontSize: 15, display: "block" };
const inputStyle = {
    padding: "12px 10px", fontSize: 17, marginTop: 2,
    borderRadius: 9, border: "1.5px solid #97c0f5",
    outline: "none", width: "100%", fontWeight: 600,
    letterSpacing: ".04em", background: "#f0f5fa", color: "#222"
};
